(() => {
  if (customElements.get('wala-hover-video')) return;
  const players = new Set();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const touchViewport = matchMedia('(hover: none), (pointer: coarse)');
  class WalaHoverVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.host = this.closest('[data-wala-hover]');
      if (!this.video || !this.host) return;
      players.add(this);
      this.card = this.host.closest('product-card, .wala-flavors__card') || this.host;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.host.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && !touchViewport.matches) this.start();
      }, options);
      this.host.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && !touchViewport.matches) {
          this.completed = false;
          this.stop();
        }
      }, options);
      this.host.addEventListener('focusin', event => {
        if (event.target.matches('a:focus-visible')) this.start();
      }, options);
      this.host.addEventListener('focusout', event => {
        if (!this.host.contains(event.relatedTarget) && !touchViewport.matches) {
          this.completed = false;
          this.stop();
        }
      }, options);
      this.host.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.dismissed = true;
          this.stop();
        }
      }, options);
      this.video.addEventListener('playing', () => {
        if (this.requested) this.host.classList.add('wala-video-playing');
        else this.video.pause();
      }, options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.stop();
      }, options);
      this.video.addEventListener('ended', () => {
        this.completed = true;
        this.stop();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.35;
        if (!entries[0].isIntersecting) {
          this.dismissed = false;
          this.completed = false;
        }
        this.refresh();
      }, { threshold: [0, 0.35] });
      this.observer.observe(this.host);
    }
    disconnectedCallback() {
      this.stop();
      players.delete(this);
      this.abort?.abort();
      this.observer?.disconnect();
    }
    refresh() {
      if (touchViewport.matches) {
        if (!this.inView || this.dismissed || document.hidden || reducedMotion.matches) {
          this.stop();
        } else if (!this.completed && !this.requested && !this.timer && !this.failed) {
          this.timer = setTimeout(() => {
            this.timer = null;
            if (this.inView && !this.dismissed && !this.completed && touchViewport.matches) this.start();
          }, 3000);
        }
      } else if (!this.inView || document.hidden || reducedMotion.matches) {
        this.stop();
      }
    }
    start() {
      if (this.failed || !this.video || this.requested || this.completed || reducedMotion.matches || document.hidden) return;
      if (!touchViewport.matches) players.forEach(player => { if (player !== this) player.stop(); });
      this.requested = true;
      this.video.muted = true;
      this.video.loop = false;
      if (!this.video.getAttribute('src')) this.video.src = this.video.dataset.src;
      this.video.play().catch(error => {
        if (error.name === 'AbortError') return;
        this.stop();
        if (error.name !== 'NotAllowedError') this.failed = true;
      });
    }
    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      this.requested = false;
      this.host?.classList.remove('wala-video-playing');
      if (this.video) {
        this.video.pause();
        if (this.video.readyState > 0) this.video.currentTime = 0;
      }
    }
  }
  document.addEventListener('visibilitychange', () => players.forEach(player => player.refresh()));
  window.addEventListener('blur', () => players.forEach(player => player.stop()));
  window.addEventListener('focus', () => players.forEach(player => player.refresh()));
  reducedMotion.addEventListener('change', () => players.forEach(player => player.refresh()));
  touchViewport.addEventListener('change', () => players.forEach(player => { player.stop(); player.refresh(); }));
  customElements.define('wala-hover-video', WalaHoverVideo);
})();
